import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/testIds";
import { Plus, Minus } from "lucide-react";
import { useT } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function Faq() {
    const { t } = useT();
    const items = t("faq.items") || [];
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" data-testid={TID.faq.root} className="bg-sn-paper py-24 md:py-40">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("faq.eyebrow")}
                    </span>
                    <span className="hairline flex-1" />
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-ink-2">
                        {t("faq.count")}
                    </span>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="md:col-span-4 font-serif text-5xl md:text-6xl leading-[0.98] tracking-tight text-sn-ink"
                    >
                        {t("faq.headline_a")}{" "}
                        <em className="italic text-sn-blue">
                            {t("faq.headline_b")}
                        </em>
                        {t("faq.headline_c")}
                    </motion.h2>

                    <div className="md:col-span-8 divide-y divide-black/10 border-t border-black/10">
                        {items.map((item, i) => {
                            const isOpen = open === i;
                            return (
                                <div key={i}>
                                    <button
                                        data-testid={TID.faq.item(i + 1)}
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
                                    >
                                        <span className="flex items-baseline gap-5">
                                            <span className="font-mono-e text-xs text-sn-blue tracking-widest">
                                                0{i + 1}
                                            </span>
                                            <span className="font-serif text-2xl md:text-3xl leading-tight text-sn-ink">
                                                {item.q}
                                            </span>
                                        </span>
                                        <span
                                            className={`shrink-0 w-9 h-9 rounded-full border border-black/20 flex items-center justify-center transition-colors duration-300 ${isOpen ? "bg-sn-blue border-sn-blue text-white" : "text-sn-ink group-hover:border-sn-blue group-hover:text-sn-blue"}`}
                                        >
                                            {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                                        </span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.5, ease: EASE }}
                                                className="overflow-hidden"
                                            >
                                                <p className="pb-8 pl-10 md:pl-14 pr-14 text-lg text-sn-ink-2 leading-relaxed max-w-2xl">
                                                    {item.a}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
